/**
 * DndEditor — headless settings sub-section that drives the per-device
 * Do Not Disturb 1-hour snooze.
 *
 * Spec: when `dndSnoozeUntil > now()`, the wire-time gate from
 * `decideDisplay` returns `"silent"` for non-bypass apps — the
 * notification lands in the panel but skips toast / sound / badge
 * bump. All state is local to this device — no server round-trip.
 *
 * Headless: prop-driven, no storage / API client / `browser.*`
 * imports. The host:
 *   - threads `snoozeUntil` from `ReflectoStorage.dndSnoozeUntil`;
 *   - implements `onSnoozeFor` / `onCancelSnooze` as
 *     `setStorage({ dndSnoozeUntil: now + ms })` / `removeStorage(...)`.
 */

import { AlarmClock, XClose } from "@untitledui/icons";
import { Button } from "../Button";
import { Text } from "../Text";
import styles from "./DndEditor.module.css";

export interface DndEditorLabels {
  /** Section heading (rendered above the editor by SettingsPanel). */
  title: string;
  /** Short description rendered above the snooze button. */
  description: string;
  /** "Snooze 1 hour" primary button label. */
  snooze1h: string;
  /** Active-until status copy. Receives the formatted time
   *  ("7:43 PM") so locales can rephrase. */
  snoozeActiveUntil: (formattedTime: string) => string;
  /** Cancel snooze button label. */
  cancelSnooze: string;
}

export interface DndEditorProps {
  /** Epoch ms timestamp; if > Date.now() the snooze is active. */
  snoozeUntil: number | undefined;
  /** Called when the user taps "Snooze 1h". Host should write
   *  `dndSnoozeUntil = Date.now() + durationMs`. */
  onSnoozeFor: (durationMs: number) => void;
  /** Called when the user taps Cancel on an active snooze. Host
   *  should remove `dndSnoozeUntil` from storage. */
  onCancelSnooze: () => void;
  /** Localized labels — every visible string. */
  labels: DndEditorLabels;
  /** Optional clock; defaults to `Date.now`. Tests pin this for
   *  deterministic active-until rendering. */
  now?: () => number;
}

/** Hour multiplier exported for hosts that need the helper for testing
 *  without re-implementing it. */
export const ONE_HOUR_MS = 60 * 60 * 1000;

function formatClockTime(epochMs: number): string {
  // Locale-formatted ("7:43 PM" in en-US, "19:43" in en-GB) so the
  // displayed time matches the user's platform conventions without
  // a custom 12/24 hour pref.
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function DndEditor({
  snoozeUntil,
  onSnoozeFor,
  onCancelSnooze,
  labels,
  now = Date.now,
}: DndEditorProps) {
  const snoozeActive = snoozeUntil != null && snoozeUntil > now();

  // One row in both states — text left, action right — so DND reads as a peer
  // of the toggle rows above it rather than a card nested inside the section.
  // While a snooze is running the status replaces the description: "active
  // until 7:43 PM" is the only line that still tells the user anything.
  return (
    <div className={styles.row}>
      {snoozeActive ? (
        <>
          <Text size="s" weight="medium" className={styles.rowText}>
            {labels.snoozeActiveUntil(formatClockTime(snoozeUntil))}
          </Text>
          <Button variant="secondary" size="s" leadingIcon={XClose} onClick={onCancelSnooze}>
            {labels.cancelSnooze}
          </Button>
        </>
      ) : (
        <>
          <Text as="p" size="s" tone="tertiary" className={styles.rowText}>
            {labels.description}
          </Text>
          <Button
            variant="secondary"
            size="s"
            leadingIcon={AlarmClock}
            onClick={() => onSnoozeFor(ONE_HOUR_MS)}
          >
            {labels.snooze1h}
          </Button>
        </>
      )}
    </div>
  );
}
